// Libraries
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Material UI
import { Container } from '@mui/material';
import Grid from '@mui/material/Grid';

// Core
import { ResourceTextApplication, paramRepository, ParameterValues } from '../core/resources';
import FormValidator from '../core/FormValidator';
import auth from '../core/authorization';

// Common
import { AAddButton, ADeleteIconButton, AEditIconButton, ASearchIconButton } from '../common/buttons';
import ViewLoader from '../common/ViewLoader';
import { ADataGrid, ADataGridActions, AGridColumns, gridColumns } from '../common/dataGrid';
import { TextWithTooltip, ViewTitle } from '../common/typography';
import { AInputContainer } from '../common/inputs';
import SearchCalendar from '../common/search/SearchCalendar';

// Interfaces
import { CompanyEntityTypeEnum } from '../interfaces/enums';
import defaultData from '../core/defaultData';
import { IMessages } from './ISms';

// Store
// API
import shortMessagesApi from '../api/shortMessageApi';

// Feature - SMS
import SendMessageFormDialog from './SendMessageDialogForm';
import CancelMessageDialog from './CancelMessageDialog';
import { SMS } from './constants/SMS';

interface MessageGridData {
    id: string;
    phoneNumber: string;
    receiverCompany: string;
    receiverPerson: string;
    content: string;
    deliveryState: string;
    messageType: string;
    requestedDeliveryDate: string | null;
    creationDate: string;
    receiverRoles: string;
}

export default function ShortMessageServiceView() {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaAccessRights',
        'AnelmaCompany',
        'AnelmaCommunication',
        'AnelmaGeneral',
        'AnelmaLayout',
    ]);
    const [farmTitles, setFarmTitles] = useState<ParameterValues>([]);
    const [logisticsTitles, setLogisticsTitles] = useState<ParameterValues>([]);
    const [snellmanTitles, setSnellmanTitles] = useState<ParameterValues>([]);
    const [senderFormVisible, setSenderFormVisible] = useState<boolean>(false);
    const [cancelFromVisible, setCancelFormVisible] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [formMode, setFormMode] = useState<'create' | 'edit' | 'read'>('edit');
    const [messages, setMessages] = useState<IMessages>(defaultData.sendDirectMessages());
    const [deliveryStateColumn, setDeliveryStateColumn] = useState<ParameterValues>([]);
    const [rowsData, setRowsData] = useState<IMessages[]>([]);
    const [calendarFromDate, setCalendarFromDate] = useState<moment.Moment>(moment().add(-1, 'M').startOf('day'));
    const [calendarToDate, setCalendarToDate] = useState<moment.Moment>(moment().endOf('day'));

    const dateFormat = 'YYYY-MM-DDTHH:mm:ss';

    useEffect(() => {
        paramRepository.load(['AnelmaGeneral']).then(() => {
            setFarmTitles(paramRepository.resource('AnelmaGeneral', 'FarmTitles'));
            setLogisticsTitles(paramRepository.resource('AnelmaGeneral', 'LogisticsTitles'));
            setSnellmanTitles(paramRepository.resource('AnelmaGeneral', 'SnellmanTitles'));
        });
    }, []);

    useEffect(() => {
        const deliveryStateParam = paramRepository.load(['AnelmaSMS']).then(() => {
            setDeliveryStateColumn(
                paramRepository.resource('AnelmaSMS', 'DeliveryStates').map((resourceParam) => {
                    return {
                        ...resourceParam,
                    };
                })
            );
        });

        const getmessagePromise = shortMessagesApi.getMessagesList(
            calendarFromDate.format(dateFormat),
            calendarToDate.format(dateFormat)
        ).then((response) => {
            setRowsData(response?.Items ?? []);
        });

        Promise.all([deliveryStateParam, getmessagePromise]).then(() => {
            setIsLoading(false);
        });
    }, []);

    const messageData: MessageGridData[] = rowsData.map((x, i) => {
        return {
            id: x.Guid,
            phoneNumber: x.Receiver.toString(),
            receiverCompany:
                x.ReceiverFarmProducerId === null || x.ReceiverFarmName === null
                    ? '***'
                    : x.ReceiverFarmProducerId + ' - ' + x.ReceiverFarmName,
            receiverPerson: x.ReceiverName || '***',
            content: x.Content,
            deliveryState: x.DeliveryState,
            messageType: x.MessageType,
            requestedDeliveryDate: x.RequestedDeliveryDate,
            creationDate: x.CreationDate,
            receiverRoles: getRoles(x.ReceiverRole, x.ReceiverFarmType),
        };
    });

    const rows = messageData
        .sort((a, b) => new Date(b.creationDate).valueOf() - new Date(a.creationDate).valueOf());

    function getRoles(relations: string[], type: number): string {
        if (relations.length === 0 || type === null) return '***';

        let titles: ParameterValues | null = null;
        var result = '';

        if (type === CompanyEntityTypeEnum.Farm) {
            titles = farmTitles;
        } else if (type === CompanyEntityTypeEnum.TrafficContractor) {
            titles = logisticsTitles;
        } else if (type === CompanyEntityTypeEnum.Snellman) {
            titles = snellmanTitles;
        } else {
            return '***';
        }

        relations.map((r) => {
            if (result.length !== 0) {
                result += ', ';
            }
            result += titles?.find((o) => o.code == r)?.text;
        });

        return result;
    }

    const getMessagesBySelectedDates = () => {
        setIsLoading(true);
        shortMessagesApi.getMessagesList(
            calendarFromDate.format(dateFormat),
            calendarToDate.format(dateFormat)
        ).then((response) => {
            setRowsData(response?.Items ?? []);
            setIsLoading(false);
        });
    };

    const handleDateChange = (from: moment.Moment, to: moment.Moment, changeFrom: boolean) => {
        var fromDate = from.toDate();
        var toDate = to.toDate();

        if (changeFrom) {
            var newFromDate = from.toDate();
            newFromDate = new Date(newFromDate.setMonth(newFromDate.getMonth() + 3));

            if (fromDate > toDate || newFromDate < toDate) {
                setCalendarToDate(moment(newFromDate).endOf('day'));
            }

            setCalendarFromDate(from.startOf('day'));
        } else {
            var newToDate = to.toDate();
            newToDate = new Date(newToDate.setMonth(newToDate.getMonth() - 3));

            if (fromDate > toDate || newToDate > fromDate) {
                setCalendarFromDate(moment(newToDate).startOf('day'));
            }

            setCalendarToDate(to.endOf('day'));
        }
    };

    const columns: AGridColumns = [
        {
            field: 'phoneNumber',
            headerName: t('AnelmaCommunication:1083'),
            type: 'string',
            width: 170,
        },
        {
            field: 'receiverCompany',
            headerName: t('AnelmaCompany:1003') + ' - ' + t('AnelmaCommunication:1070'),
            type: 'string',
            width: 220,
            renderCell: (params) => {
                return <TextWithTooltip text={params.value as string} />;
            },
        },
        {
            field: 'receiverPerson',
            headerName: t('AnelmaGeneral:1014'),
            type: 'string',
            width: 130,
            renderCell: (params) => {
                return <TextWithTooltip text={params.value as string} />;
            },
        },
        {
            field: 'receiverRoles',
            headerName: t('AnelmaCommunication:1072'),
            type: 'string',
            width: 160,
            renderCell: (params) => {
                return <TextWithTooltip text={params.value as string} />;
            },
        },
        {
            field: 'content',
            headerName: t('AnelmaCommunication:1069'),
            type: 'string',
            flex: 1,
            renderCell: (params) => {
                return <TextWithTooltip text={params.value as string} />;
            },
        },
        {
            field: 'deliveryState',
            headerName: t('AnelmaCommunication:1038'),
            type: 'string',
            width: 115,
            renderCell: (params) => {
                return (
                    <TextWithTooltip
                        text={String(
                            deliveryStateColumn.find(
                                (p) => p.code === (params.row as MessageGridData).deliveryState
                            )?.text ?? ''
                        )}
                    />
                );
            },
        },

        gridColumns.dateTime(
            'requestedDeliveryDate',
            t('AnelmaCommunication:1076'),
            'DD.MM.YYYY HH:mm',
            130
        ),
    ];

    const smsRowActions: ADataGridActions = [
        (params) => deleteButton(params.row),
        (params) => editOrSearchButton(params.row)
    ];

    const deleteButton = (row: MessageGridData): JSX.Element => {
        return canDelete(row) ? (
            <ADeleteIconButton
                key={`${row.id}-delete`}
                onClick={() => deleteMessageFromList(row)}
                tooltip={t('AnelmaGeneral:1012')}
            />
        ) : (
            <></>
        );
    }

    const editOrSearchButton = (row: MessageGridData): JSX.Element => {
        if (isPending(row) && auth.canUpdateSms)
            return (
                <AEditIconButton
                    key={`${row.id}-edit`}
                    onClick={() => openEdit(row)}
                    tooltip={t('AnelmaGeneral:1075')}
                />
            );

        return (
            <ASearchIconButton
                key={`${row.id}-search`}
                onClick={() => openRead(row)}
                tooltip={t('AnelmaGeneral:1068')}
                disabled={!auth.canViewSms}
            />
        );
    };

    const openEdit = (row: MessageGridData) => {
        let i = -1;
        i = rowsData.findIndex(r => r.Guid === row.id)

        if (i !== -1) {
            setMessages(rowsData[i]);
            setFormMode('edit');
            setSenderFormVisible(true);
        }
    }

    const openRead = (row: MessageGridData) => {
        let i = -1;
        i = rowsData.findIndex(r => r.Guid === row.id)

        if (i !== -1) {
            setMessages(rowsData[i]);
            setFormMode('read');
            setSenderFormVisible(true);
        }
    }

    const deleteMessageFromList = (row: MessageGridData) => {
        let i = -1;
        i = rowsData.findIndex(r => r.Guid === row.id)

        if (i !== -1) {
            setMessages(rowsData[i]);
            setCancelFormVisible(true);
        }
    }

    const canDelete = (row: MessageGridData) => {
        return isPending(row)
            && auth.canChancelSms;
    }

    const isPending = (row: MessageGridData) => {
        return row.messageType == 'ScheduledMessage'
            && row.deliveryState == '02';
    }

    return (
        <>
            <Container data-robot-id={'app-body-management-msm'}>
                <ViewTitle>{t('AnelmaLayout:1018')}</ViewTitle>
                <Grid container spacing={0}>
                    <Grid size={{ xs: 12 }} style={{ marginBottom: '20px', textAlign: 'right' }}>
                        <AInputContainer>
                            <AAddButton
                                type='action'
                                onClick={() => {
                                    setFormMode('create');
                                    setSenderFormVisible(!senderFormVisible);
                                    setMessages(defaultData.sendDirectMessages());
                                }}
                                disabled={!auth.canCreateSms}
                            >
                                {t('AnelmaCommunication:1061')}
                            </AAddButton>
                        </AInputContainer>
                    </Grid>
                </Grid>

                {senderFormVisible && (
                    <SendMessageFormDialog
                        mode={formMode}
                        data={messages}
                        onClose={(update) => {
                            setFormMode('create');
                            setSenderFormVisible(false);
                            setMessages(defaultData.sendDirectMessages());
                            if (update)
                                getMessagesBySelectedDates();
                        }}
                        validator={new FormValidator()}
                    />
                )}
                <SearchCalendar
                    handleSort={() => getMessagesBySelectedDates()}
                    handleFromDateChange={(fromDate) =>
                        handleDateChange(fromDate, calendarToDate, true)
                    }
                    handleToDateChange={(toDate) =>
                        handleDateChange(calendarFromDate, toDate, false)
                    }
                    fromDate={calendarFromDate}
                    toDate={calendarToDate}
                    className={SMS.SearchCalendar}
                    childrenText={t('AnelmaCommunication:1160')}
                    fromDateId={SMS.SearchCalendarFromDate}
                    toDateId={SMS.SearchCalendarToDate}
                />

                {cancelFromVisible && (
                    <CancelMessageDialog
                        id={messages.Guid}
                        afterDelete={(guid) => {
                            const removeSMS = [...rowsData];
                            const index = removeSMS.findIndex((p) => p.Guid === guid);
                            removeSMS.splice(index, 1);
                            setRowsData(removeSMS);
                        }}
                        onClose={() => {
                            setCancelFormVisible(!cancelFromVisible);
                            getMessagesBySelectedDates();
                        }}
                    />
                )}

                {!isLoading ? (
                    <ADataGrid
                        columns={columns}
                        rows={rows}
                        actions={smsRowActions}
                        enableFiltering
                        enableRowNumbers
                        enablePagination
                    />
                ) : (
                    <ViewLoader />
                )}
            </Container>
        </>
    );
}
